import React from 'react';
import Image from 'next/image';
import { Box, Stack, styled, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Container } from '@/components/layout';
import Icon from '@/components/icons/Icon';
import { AuthStore, OrgStore } from '@/stores/index';
import { useInitChat } from '@/hooks/useInitChat';

const HeroGrid = styled('div')`
  display: grid;
  grid-template-areas:
    'block-1 block-3'
    'block-2 block-3';
  grid-template-columns: 1fr 1fr;
  column-gap: 3rem;
  row-gap: 2.25rem;

  .block-1 {
    grid-area: block-1;
  }
  .block-2 {
    grid-area: block-2;
  }
  .block-3 {
    grid-area: block-3;
  }

  .block-2 {
    margin-bottom: 2.5rem;
  }

  @media (max-width: 768px) {
    column-gap: 0;
    row-gap: 1.5rem;
    grid-template-areas:
      'block-1'
      'block-3'
      'block-2';
    grid-template-columns: 1fr;

    .block-2 {
      margin-bottom: 0;
    }
  }
`;

function Hero() {
  const theme = useTheme();

  const { isLoggedIn } = AuthStore;

  const initChat = useInitChat({
    message: OrgStore.accManager?.intercom_chat_init_message,
    slug: OrgStore.accManager?.slug,
  });

  return (
    <Stack
      component="section"
      sx={{
        backgroundColor: theme.palette.grey[100],
      }}
    >
      <Container>
        <HeroGrid
          sx={{
            paddingTop: { xs: theme.spacing(6), md: theme.spacing(12.5) },
            paddingBottom: { xs: theme.spacing(10), md: theme.spacing(7.5) },
          }}
        >
          <Stack
            sx={{
              gap: { xs: theme.spacing(4), md: theme.spacing(5) },
            }}
            className="block-1"
          >
            <Typography
              fontSize="3.125rem !important"
              lineHeight="1.18 !important"
              variant="h1"
              color={theme.palette.common.white}
              sx={{
                color: '#34353E',
                fontSize: {
                  xs: '2.375rem !important',
                  md: '3.125rem !important',
                },
                lineHeight: { xs: '1.2 !important', md: '1.18 !important' },
              }}
            >
              What can we help you with?
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#34353E',
                fontSize: '1.125rem !important',
                lineHeight: '1.46 !important',
              }}
            >
              Need help with your OneVillage experience? Our dedicated support
              team is here to guide you every step of the way, ensuring you get
              the most out of your journey.
            </Typography>
          </Stack>

          <Stack
            sx={{
              gap: theme.spacing(4),
              alignItems: 'stretch',
              flexDirection: { xs: 'column', md: 'row' },
            }}
            className="block-2"
          >
            <Stack
              component="a"
              href="mailto: support@onevillage.io"
              sx={{
                gap: theme.spacing(1.5),
                border: '1px solid #0000001A',
                borderRadius: theme.spacing(4),
                padding: theme.spacing(4, 1, 5.5),
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: theme.palette.common.white,
                flex: 1,
              }}
            >
              <Stack
                sx={{
                  gap: theme.spacing(2),
                  flexDirection: 'row',
                }}
              >
                <Icon
                  iconName="email"
                  style={{
                    color: theme.palette.primary.main,
                    width: theme.spacing(7.5),
                    height: theme.spacing(7.5),
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: '1.4375rem !important',
                    fontWeight: '700',
                    lineHeight: '1.4 !important',
                  }}
                >
                  Email
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  color: '#52545F',
                  fontSize: '0.875rem !important',
                  fontWeight: '700',
                  lineHeight: '1.225rem !important',
                }}
              >
                support@onevillage.io
              </Typography>
            </Stack>

            <Stack
              component="a"
              href="tel: +12024306797"
              sx={{
                gap: theme.spacing(1.5),
                border: '1px solid #0000001A',
                borderRadius: theme.spacing(4),
                padding: theme.spacing(4, 1, 5.5),
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                backgroundColor: theme.palette.common.white,
              }}
            >
              <Stack
                sx={{
                  gap: theme.spacing(2),
                  flexDirection: 'row',
                }}
              >
                <Icon
                  iconName="call"
                  style={{
                    color: theme.palette.primary.main,
                    width: theme.spacing(7.5),
                    height: theme.spacing(7.5),
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: '1.4375rem !important',
                    fontWeight: '700',
                    lineHeight: '1.4 !important',
                  }}
                >
                  Phone
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  color: '#52545F',
                  fontSize: '0.875rem !important',
                  fontWeight: '700',
                  lineHeight: '1.225rem !important',
                }}
              >
                +1 (202) 430-6797
              </Typography>
            </Stack>

            <Stack
              {...(isLoggedIn
                ? {
                    component: 'button',
                    onClick: initChat,
                  }
                : {
                    component: 'div',
                  })}
              sx={{
                gap: theme.spacing(1.5),
                borderRadius: theme.spacing(4),
                padding: theme.spacing(4, 1, 5.5),
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                ...(isLoggedIn
                  ? {
                      backgroundColor: theme.palette.common.white,
                      border: '1px solid #0000001A',
                    }
                  : {
                      backgroundColor: theme.palette.warning[500],
                    }),
              }}
            >
              <Stack
                sx={{
                  gap: theme.spacing(2),
                  flexDirection: 'row',
                }}
              >
                <Icon
                  iconName="start-chat-2"
                  style={{
                    color: isLoggedIn
                      ? theme.palette.primary.main
                      : theme.palette.warning[800],
                    width: theme.spacing(7.5),
                    height: theme.spacing(7.5),
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    color: isLoggedIn
                      ? theme.palette.primary.main
                      : theme.palette.warning[800],
                    fontSize: '1.4375rem !important',
                    fontWeight: '700',
                    lineHeight: '1.4 !important',
                  }}
                >
                  Chat
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  color: isLoggedIn ? '#52545F' : theme.palette.secondary[900],
                  fontSize: '0.875rem !important',
                  fontWeight: '700',
                  lineHeight: '1.225rem !important',
                  ...(isLoggedIn
                    ? {
                        color: '#52545F',
                        fontSize: '0.875rem !important',
                      }
                    : {
                        fontSize: '0.75rem !important',
                        color: theme.palette.secondary[900],
                      }),
                }}
              >
                {isLoggedIn
                  ? 'support@onevillage.io'
                  : 'Available for authorized users'}
              </Typography>
            </Stack>
          </Stack>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 'auto',
              paddingTop: 'calc(0.624 * 100%)',
            }}
            className="block-3"
          >
            <Image
              src="/images/contact-us/hero-contact-us.png"
              layout="fill"
              objectFit="contain"
              objectPosition="center"
            />
          </Box>
        </HeroGrid>
      </Container>
    </Stack>
  );
}

export default observer(Hero);
